<script setup lang="ts">
import {onBeforeUnmount, onMounted, ref} from 'vue'
import Button from '@/components/www/primitives/Button.vue'
import Container from '@/components/www/primitives/Container.vue'
import Logo from '@/components/www/layout/Logo.vue'

/**
 * Paid-landing chrome: logo (same-page top) + one primary CTA.
 * Opt in with route meta `landingChrome`. Do not use the site Header here.
 */
const props = withDefaults(defineProps<{
    theme?: 'light' | 'dark'
    logoTo: string
    logoLabel?: string
    /** When false, the logo follows `logoTo` instead of scrolling this page to the top. */
    logoScrollsToTop?: boolean
    ctaLabel: string
    ctaTo: string
    /** Overrides the default `demo_request` click id. Omit to keep the shared header. */
    ctaId?: string
    /** Shorter bar after scroll. The top-of-page header is unchanged. */
    slimWhenScrolled?: boolean
}>(), {
    theme: 'light',
    logoLabel: 'Back to top',
    logoScrollsToTop: true,
    ctaId: undefined,
    slimWhenScrolled: false,
})

// Bare over the hero, filled once the page scrolls under it — same "transparent
// at top" behavior as the site Header, minus its hide-on-scroll-down (this
// header's single CTA has to stay reachable).
const THRESHOLD = 24 // px
const scrolled = ref(false)
let ticking = false

function updateScrolled(): void {
    ticking = false
    scrolled.value = window.scrollY > THRESHOLD
}

function onScroll(): void {
    if (ticking) return
    ticking = true
    requestAnimationFrame(updateScrolled)
}

onMounted(() => {
    updateScrolled()
    window.addEventListener('scroll', onScroll, {passive: true})
})

onBeforeUnmount(() => window.removeEventListener('scroll', onScroll))

function onLogoClick(event: MouseEvent): void {
    if (!props.logoScrollsToTop) return
    event.preventDefault()
    window.scrollTo({top: 0})
}
</script>

<template>
  <header
      class="landing-header"
      :data-header-theme="theme"
      :data-header-state="scrolled ? 'scrolled' : 'top'"
      :data-header-slim="slimWhenScrolled && scrolled ? '' : undefined"
      data-landing-header
  >
    <Container width="lg" class="landing-header__inner">
      <Logo
          class="landing-header__logo"
          :to="logoTo"
          :label="logoLabel"
          @click="onLogoClick"
      />
      <Button
          variant="primary"
          class="landing-header__cta"
          :to="ctaTo"
          :data-cta="ctaId ?? 'demo_request'"
          data-cta-location="header"
      >
        {{ ctaLabel }}
      </Button>
    </Container>
  </header>
</template>

<style scoped>
.landing-header {
    position: fixed;
    inset-block-start: 0;
    inset-inline: 0;
    z-index: 50;
    padding-block: var(--space-7);
    background: transparent;
    border-bottom: 1px solid transparent;
    transition:
        background-color 200ms var(--ease-out),
        border-color 200ms var(--ease-out);
}

/* Dark theme keeps its light foreground at the top of the page too — it sits
   over the hero gradient there, not over the page background. */
.landing-header[data-header-theme="dark"] {
    --header-fg: rgba(255, 255, 255, 0.92);
}

.landing-header[data-header-state="scrolled"][data-header-theme="light"] {
    background: var(--surface);
    border-bottom-color: var(--header-stroke);
}

.landing-header[data-header-state="scrolled"][data-header-theme="dark"] {
    background: color-mix(in srgb, var(--secondary-700) 92%, transparent);
    border-bottom-color: rgba(255, 255, 255, 0.1);
    -webkit-backdrop-filter: blur(12px) saturate(1.15);
    backdrop-filter: blur(12px) saturate(1.15);
}

.landing-header[data-header-slim] {
    padding-block: var(--space-3);
}

.landing-header[data-header-slim] :deep(.landing-header__logo .logo__mark) {
    height: calc(var(--logo-mark-height) * 0.72);
}

.landing-header[data-header-slim] :deep(.landing-header__cta) {
    padding: 10px 18px;
}

@media (prefers-reduced-motion: reduce) {
    .landing-header {
        transition: none;
    }
}

.landing-header :deep(.landing-header__inner) {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--space-8);
    max-width: var(--container-page-lg);
}

.landing-header :deep(.landing-header__logo) {
    margin-bottom: calc(-1 * var(--logo-mark-ink-bottom-inset));
}

.landing-header :deep(.landing-header__logo .logo__mark) {
    height: var(--logo-mark-height);
}

.landing-header[data-header-theme="light"] :deep(.logo) {
    color: var(--primary-500);
}

.landing-header[data-header-theme="dark"] :deep(.logo) {
    color: rgba(255, 255, 255, 0.95);
}

.landing-header :deep(.landing-header__cta) {
    flex-shrink: 0;
    padding: 14px 28px;
    border-radius: var(--radius-button);
    font-weight: var(--fw-semibold);
    line-height: 1;
    transition:
        transform 160ms var(--ease-out-strong),
        background-color 180ms var(--ease-out);
}

.landing-header[data-header-theme="dark"] :deep(.landing-header__cta.btn--primary) {
    background-color: var(--text-white);
    color: var(--secondary-500);
}

@media (hover: hover) and (pointer: fine) {
    .landing-header :deep(.landing-header__cta:hover) {
        transform: translateY(-1px);
    }

    .landing-header[data-header-theme="dark"] :deep(.landing-header__cta.btn--primary:hover) {
        background-color: rgba(247, 245, 242, 0.85);
    }
}

.landing-header :deep(.landing-header__cta:active) {
    transform: scale(0.97);
}

@media (max-width: 767px) {
    .landing-header :deep(.landing-header__inner) {
        align-items: center;
        gap: var(--space-4);
    }

    .landing-header :deep(.landing-header__logo) {
        margin-bottom: 0;
    }

    .landing-header :deep(.landing-header__logo .logo__mark) {
        height: calc(var(--logo-mark-height) * 0.7);
    }

    .landing-header :deep(.landing-header__cta) {
        padding: 12px 14px;
        font-size: 14px;
    }
}

@media (max-width: 360px) {
    .landing-header :deep(.landing-header__logo .logo__mark) {
        height: calc(var(--logo-mark-height) * 0.58);
    }

    .landing-header :deep(.landing-header__cta) {
        padding: 10px 12px;
        font-size: 13px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .landing-header :deep(.landing-header__cta) {
        transition: none;
    }

    .landing-header :deep(.landing-header__cta:hover),
    .landing-header :deep(.landing-header__cta:active) {
        transform: none;
    }
}
</style>
